import { useMemo } from 'react';

import { paths } from 'src/routes/paths';

import { hasPermission } from 'src/utils/has-permissions';

import { useTranslate } from 'src/locales';
import { useAuthContext } from 'src/auth/hooks';
import { UserStatus } from 'src/redux/slices/users';
import { AuthPermissions } from 'src/auth/permissions';

import SvgColor from 'src/components/svg-color';

const icon = (name: string) => (
  <SvgColor src={`/assets/icons/navbar/${name}.svg`} sx={{ width: 1, height: 1 }} />
  // OR
  // <Iconify icon="fluent:mail-24-filled" />
  // https://icon-sets.iconify.design/solar/
  // https://www.streamlinehq.com/icons
);

const ICONS = {
  job: icon('ic_job'),
  blog: icon('ic_blog'),
  chat: icon('ic_chat'),
  mail: icon('ic_mail'),
  user: icon('ic_user'),
  file: icon('ic_file'),
  lock: icon('ic_lock'),
  tour: icon('ic_tour'),
  order: icon('ic_order'),
  label: icon('ic_label'),
  blank: icon('ic_blank'),
  kanban: icon('ic_kanban'),
  folder: icon('ic_folder'),
  banking: icon('ic_banking'),
  booking: icon('ic_booking'),
  invoice: icon('ic_invoice'),
  product: icon('ic_product'),
  project: icon('ic_product'),
  calendar: icon('ic_calendar'),
  disabled: icon('ic_disabled'),
  external: icon('ic_external'),
  menuItem: icon('ic_menu_item'),
  ecommerce: icon('ic_ecommerce'),
  analytics: icon('ic_analytics'),
  dashboard: icon('ic_dashboard'),
};

// ----------------------------------------------------------------------

export function useNavData() {
  const { t } = useTranslate();

  const { user: authUser } = useAuthContext();

  const data = useMemo(() => {
    const navItems = [
      {
        subheader: t('dashboard'),
        items: [
          {
            title: t('myTasks.pageTitle'),
            path: paths.dashboard.myTasks,
            icon: ICONS.kanban,
          },
          {
            title: t('analytics'),
            path: paths.dashboard.analytics,
            icon: ICONS.analytics,
          },
        ],
      },
    ];

    // Management Section
    const managementSection: {
      subheader: string;
      items: {
        title: string;
        path: string;
        icon: JSX.Element;
        children?: { title: string; path: string }[];
      }[];
    } = {
      subheader: t('management'),
      items: [],
    };

    if (
      hasPermission(
        authUser,
        'any',
        AuthPermissions.READ_USER,
        AuthPermissions.ADMIN_PERMISSION,
        AuthPermissions.SUPER_ADMIN_PERMISSION
      )
    ) {
      managementSection.items.push({
        title: t('user.pageTitle'),
        path: paths.dashboard.user.root(UserStatus.ACTIVE),
        icon: ICONS.user,
      });
    }

    if (
      hasPermission(
        authUser,
        'any',
        AuthPermissions.READ_SUPPLIER,
        AuthPermissions.ADMIN_PERMISSION,
        AuthPermissions.SUPER_ADMIN_PERMISSION
      )
    ) {
      managementSection.items.push({
        title: t('suppliers.pageTitle'),
        path: paths.dashboard.suppliers.root,
        icon: ICONS.blank,
      });
    }

    if (
      hasPermission(
        authUser,
        'any',
        AuthPermissions.READ_EMPLOYEE,
        AuthPermissions.ADMIN_PERMISSION,
        AuthPermissions.SUPER_ADMIN_PERMISSION
      )
    ) {
      managementSection.items.push({
        title: t('employees.pageTitle'),
        path: paths.dashboard.employees.root,
        icon: ICONS.user,
      });
    }

    if (
      hasPermission(
        authUser,
        'any',
        AuthPermissions.READ_ADVANCED_PAYMENT,
        AuthPermissions.ADMIN_PERMISSION,
        AuthPermissions.SUPER_ADMIN_PERMISSION
      )
    ) {
      managementSection.items.push({
        title: t('advancedPayments.pageTitle'),
        path: paths.dashboard.advancedPayments.root,
        icon: ICONS.file,
      });
    }

    if (
      hasPermission(
        authUser,
        'any',
        AuthPermissions.READ_LOAN_AGREEMENT,
        AuthPermissions.ADMIN_PERMISSION,
        AuthPermissions.SUPER_ADMIN_PERMISSION
      )
    ) {
      managementSection.items.push({
        title: t('loans.pageTitle'),
        path: paths.dashboard.loans.root(),
        icon: ICONS.banking,
      });
    }

    // Admin-only — no seeded permission gates this, deliberately (see
    // WORKFLOW_GUIDE.md), same reasoning as Stock Count: spans Inventory,
    // Packaging and Stationery items, so it lives in Management rather
    // than any one of those sections.
    if (
      hasPermission(
        authUser,
        'any',
        AuthPermissions.ADMIN_PERMISSION,
        AuthPermissions.SUPER_ADMIN_PERMISSION
      )
    ) {
      managementSection.items.push({
        title: t('reorderSettings.pageTitle'),
        path: paths.dashboard.reorderSettings.root,
        icon: ICONS.analytics,
      });
    }

    const packagingSection: {
      subheader: string;
      items: {
        title: string;
        path: string;
        icon: JSX.Element;
        children?: { title: string; path: string }[];
      }[];
    } = {
      subheader: t('packaging.pageTitle'),
      items: [],
    };

    const stationerySection: {
      subheader: string;
      items: {
        title: string;
        path: string;
        icon: JSX.Element;
        children?: { title: string; path: string }[];
      }[];
    } = {
      subheader: t('stationery.pageTitle'),
      items: [],
    };

    const kitchenSection: {
      subheader: string;
      items: {
        title: string;
        path: string;
        icon: JSX.Element;
        children?: { title: string; path: string }[];
      }[];
    } = {
      subheader: t('kitchen.pageTitle'),
      items: [],
    };

    const chequeSection: {
      subheader: string;
      items: {
        title: string;
        path: string;
        icon: JSX.Element;
        children?: { title: string; path: string }[];
      }[];
    } = {
      subheader: t('cheque.pageTitle'),
      items: [],
    };

    const chequeFactoringSection: {
      subheader: string;
      items: {
        title: string;
        path: string;
        icon: JSX.Element;
        children?: { title: string; path: string }[];
      }[];
    } = {
      subheader: t('chequeFactoring.pageTitle'),
      items: [],
    };

    const auditSection: {
      subheader: string;
      items: {
        title: string;
        path: string;
        icon: JSX.Element;
        children?: { title: string; path: string }[];
      }[];
    } = {
      subheader: t('auditLog.pageTitle'),
      items: [],
    };

    if (
      hasPermission(
        authUser,
        'any',
        AuthPermissions.READ_AUDIT,
        AuthPermissions.ADMIN_PERMISSION,
        AuthPermissions.SUPER_ADMIN_PERMISSION
      )
    ) {
      auditSection.items.push({
        title: t('auditLog.list'),
        path: paths.dashboard.auditLog.root,
        icon: ICONS.lock,
      });
    }

    if (
      hasPermission(
        authUser,
        'any',
        AuthPermissions.READ_CHEQUE,
        AuthPermissions.ADMIN_PERMISSION,
        AuthPermissions.SUPER_ADMIN_PERMISSION
      )
    ) {
      chequeSection.items.push({
        title: t('cheque.list'),
        path: paths.dashboard.cheque.root(),
        icon: ICONS.banking,
      });
    }

    if (
      hasPermission(
        authUser,
        'any',
        AuthPermissions.READ_ISSUED_CHEQUE,
        AuthPermissions.ADMIN_PERMISSION,
        AuthPermissions.SUPER_ADMIN_PERMISSION
      )
    ) {
      chequeSection.items.push({
        title: t('cheque.issued'),
        path: paths.dashboard.issuedCheque.root,
        icon: ICONS.calendar,
      });
    }

    if (
      hasPermission(
        authUser,
        'any',
        AuthPermissions.READ_FACTORING_CANDIDATES,
        AuthPermissions.ADMIN_PERMISSION,
        AuthPermissions.SUPER_ADMIN_PERMISSION
      )
    ) {
      chequeFactoringSection.items.push({
        title: t('chequeFactoring.candidates'),
        path: paths.dashboard.chequeFactoring.candidates,
        icon: ICONS.banking,
      });
    }

    if (
      hasPermission(
        authUser,
        'any',
        AuthPermissions.READ_FACTORING_BATCH,
        AuthPermissions.ADMIN_PERMISSION,
        AuthPermissions.SUPER_ADMIN_PERMISSION
      )
    ) {
      chequeFactoringSection.items.push({
        title: t('chequeFactoring.batches'),
        path: paths.dashboard.chequeFactoring.batches.root,
        icon: ICONS.order,
      });
    }

    if (
      hasPermission(
        authUser,
        'any',
        AuthPermissions.READ_FACTORING_OVERDUE,
        AuthPermissions.ADMIN_PERMISSION,
        AuthPermissions.SUPER_ADMIN_PERMISSION
      )
    ) {
      chequeFactoringSection.items.push({
        title: t('chequeFactoring.overdue'),
        path: paths.dashboard.chequeFactoring.overdue,
        icon: ICONS.disabled,
      });
    }

    if (
      hasPermission(
        authUser,
        'any',
        AuthPermissions.READ_FACTORING_DEBTOR,
        AuthPermissions.ADMIN_PERMISSION,
        AuthPermissions.SUPER_ADMIN_PERMISSION
      )
    ) {
      chequeFactoringSection.items.push({
        title: t('chequeFactoring.debtors'),
        path: paths.dashboard.chequeFactoring.debtors,
        icon: ICONS.user,
      });
    }

    // const barSection: {
    //   subheader: string;
    //   items: {
    //     title: string;
    //     path: string;
    //     icon: JSX.Element;
    //     children?: { title: string; path: string }[];
    //   }[];
    // } = {
    //   subheader: t('bar.pageTitle'),
    //   items: [],
    // };

    if (
      hasPermission(
        authUser,
        'any',
        AuthPermissions.READ_PRODUCT,
        AuthPermissions.ADMIN_PERMISSION,
        AuthPermissions.SUPER_ADMIN_PERMISSION
      )
    ) {
      packagingSection.items.push({
        title: t('packaging.product'),
        path: paths.dashboard.packagingProduct.root,
        icon: ICONS.product,
      });
    }

    if (
      hasPermission(
        authUser,
        'any',
        AuthPermissions.READ_PACKAGING_ITEM,
        AuthPermissions.ADMIN_PERMISSION,
        AuthPermissions.SUPER_ADMIN_PERMISSION
      )
    ) {
      packagingSection.items.push({
        title: t('packaging.item'),
        path: paths.dashboard.packagingItem.root,
        icon: ICONS.product,
      });
    }

    if (
      hasPermission(
        authUser,
        'any',
        AuthPermissions.READ_PACKAGING_PURCHASE_ORDER,
        AuthPermissions.ADMIN_PERMISSION,
        AuthPermissions.SUPER_ADMIN_PERMISSION
      )
    ) {
      packagingSection.items.push({
        title: t('packaging.purchase'),
        path: paths.dashboard.packagingPurchaseOrder.root,
        icon: ICONS.order,
      });
    }

    if (
      hasPermission(
        authUser,
        'any',
        AuthPermissions.READ_PACKAGING_DISPATCH,
        AuthPermissions.ADMIN_PERMISSION,
        AuthPermissions.SUPER_ADMIN_PERMISSION
      )
    ) {
      packagingSection.items.push({
        title: t('packaging.dispatch'),
        path: paths.dashboard.packagingDispatch.root,
        icon: ICONS.external,
      });
    }

    if (
      hasPermission(
        authUser,
        'any',
        AuthPermissions.READ_PACKAGING_RETURN,
        AuthPermissions.ADMIN_PERMISSION,
        AuthPermissions.SUPER_ADMIN_PERMISSION
      )
    ) {
      packagingSection.items.push({
        title: t('packaging.return'),
        path: paths.dashboard.packagingReturn.root,
        icon: ICONS.external,
      });
    }

    // Admin-only — no regular seeded permission gates this, deliberately
    // (see WORKFLOW_GUIDE.md): a stock count directly overwrites a running
    // total from a manual recount, so it isn't a permission any staff
    // member should be grantable, unlike every other nav entry here.
    if (
      hasPermission(
        authUser,
        'any',
        AuthPermissions.ADMIN_PERMISSION,
        AuthPermissions.SUPER_ADMIN_PERMISSION
      )
    ) {
      packagingSection.items.push({
        title: t('packaging.stockCount'),
        path: paths.dashboard.packagingStockCount.root,
        icon: ICONS.analytics,
      });
    }

    if (
      hasPermission(
        authUser,
        'any',
        AuthPermissions.READ_PACKAGING_DAMAGE_DISPATCH,
        AuthPermissions.ADMIN_PERMISSION,
        AuthPermissions.SUPER_ADMIN_PERMISSION
      )
    ) {
      packagingSection.items.push({
        title: t('packaging.damageDispatch'),
        path: paths.dashboard.packagingDamageDispatch.root,
        icon: ICONS.external,
      });
    }

    // Admin-only — no seeded permission gates this, deliberately (see
    // WORKFLOW_GUIDE.md), same reasoning as Stock Count above.
    if (
      hasPermission(
        authUser,
        'any',
        AuthPermissions.ADMIN_PERMISSION,
        AuthPermissions.SUPER_ADMIN_PERMISSION
      )
    ) {
      packagingSection.items.push({
        title: t('packaging.usage'),
        path: paths.dashboard.packagingItem.usage,
        icon: ICONS.analytics,
      });
    }

    if (
      hasPermission(
        authUser,
        'any',
        AuthPermissions.READ_STATIONERY_ITEM,
        AuthPermissions.ADMIN_PERMISSION,
        AuthPermissions.SUPER_ADMIN_PERMISSION
      )
    ) {
      stationerySection.items.push({
        title: t('stationery.item'),
        path: paths.dashboard.stationeryItem.root,
        icon: ICONS.product,
      });
    }

    if (
      hasPermission(
        authUser,
        'any',
        AuthPermissions.READ_STATIONERY_PURCHASE_ORDER,
        AuthPermissions.ADMIN_PERMISSION,
        AuthPermissions.SUPER_ADMIN_PERMISSION
      )
    ) {
      stationerySection.items.push({
        title: t('stationery.purchase'),
        path: paths.dashboard.stationeryPurchaseOrder.root,
        icon: ICONS.order,
      });
    }

    if (
      hasPermission(
        authUser,
        'any',
        AuthPermissions.READ_STATIONERY_DISPATCH,
        AuthPermissions.ADMIN_PERMISSION,
        AuthPermissions.SUPER_ADMIN_PERMISSION
      )
    ) {
      stationerySection.items.push({
        title: t('stationery.dispatch'),
        path: paths.dashboard.stationeryDispatch.root,
        icon: ICONS.external,
      });
    }

    if (
      hasPermission(
        authUser,
        'any',
        AuthPermissions.READ_STATIONERY_RETURN,
        AuthPermissions.ADMIN_PERMISSION,
        AuthPermissions.SUPER_ADMIN_PERMISSION
      )
    ) {
      stationerySection.items.push({
        title: t('stationery.return'),
        path: paths.dashboard.stationeryReturn.root,
        icon: ICONS.external,
      });
    }

    // Admin-only — same deliberate divergence as the packaging stock count
    // entry above.
    if (
      hasPermission(
        authUser,
        'any',
        AuthPermissions.ADMIN_PERMISSION,
        AuthPermissions.SUPER_ADMIN_PERMISSION
      )
    ) {
      stationerySection.items.push({
        title: t('stationery.stockCount'),
        path: paths.dashboard.stationeryStockCount.root,
        icon: ICONS.analytics,
      });
    }

    if (
      hasPermission(
        authUser,
        'any',
        AuthPermissions.READ_STATIONERY_DAMAGE_DISPATCH,
        AuthPermissions.ADMIN_PERMISSION,
        AuthPermissions.SUPER_ADMIN_PERMISSION
      )
    ) {
      stationerySection.items.push({
        title: t('stationery.damageDispatch'),
        path: paths.dashboard.stationeryDamageDispatch.root,
        icon: ICONS.external,
      });
    }

    if (
      hasPermission(
        authUser,
        'any',
        AuthPermissions.READ_KITCHEN_ITEM,
        AuthPermissions.ADMIN_PERMISSION,
        AuthPermissions.SUPER_ADMIN_PERMISSION
      )
    ) {
      kitchenSection.items.push({
        title: t('kitchen.list'),
        path: paths.dashboard.kitchen.root(),
        icon: ICONS.product,
      });
    }

    if (
      hasPermission(
        authUser,
        'any',
        AuthPermissions.READ_KITCHEN_INVENTORY_TRANSACTION,
        AuthPermissions.ADMIN_PERMISSION,
        AuthPermissions.SUPER_ADMIN_PERMISSION
      )
    ) {
      kitchenSection.items.push({
        title: t('kitchen.purchase'),
        path: paths.dashboard.kitchen.purchase.root(),
        icon: ICONS.order,
      });

      kitchenSection.items.push({
        title: t('kitchen.dispatch'),
        path: paths.dashboard.kitchen.dispatch.root(),
        icon: ICONS.external,
      });
    }

    // if (
    //   hasPermission(
    //     authUser,
    //     'any',
    //     AuthPermissions.READ_BAR_ITEM,
    //     AuthPermissions.ADMIN_PERMISSION,
    //     AuthPermissions.SUPER_ADMIN_PERMISSION
    //   )
    // ) {
    //   barSection.items.push({
    //     title: t('bar.list'),
    //     path: paths.dashboard.bar.root(),
    //     icon: ICONS.product,
    //   });
    // }

    // if (
    //   hasPermission(
    //     authUser,
    //     'any',
    //     AuthPermissions.READ_BAR_INVENTORY_TRANSACTION,
    //     AuthPermissions.ADMIN_PERMISSION,
    //     AuthPermissions.SUPER_ADMIN_PERMISSION
    //   )
    // ) {
    //   barSection.items.push({
    //     title: t('bar.purchase'),
    //     path: paths.dashboard.bar.purchase.root(),
    //     icon: ICONS.order,
    //   });

    //   barSection.items.push({
    //     title: t('bar.dispatch'),
    //     path: paths.dashboard.bar.dispatch.root(),
    //     icon: ICONS.external,
    //   });
    // }

    if (managementSection.items.length > 0) {
      navItems.push(managementSection);
    }

    if (chequeSection.items.length > 0) {
      navItems.push(chequeSection);
    }

    if (chequeFactoringSection.items.length > 0) {
      navItems.push(chequeFactoringSection);
    }

    if (auditSection.items.length > 0) {
      navItems.push(auditSection);
    }

    if (kitchenSection.items.length > 0) {
      navItems.push(kitchenSection);
    }

    if (packagingSection.items.length > 0) {
      navItems.push(packagingSection);
    }

    if (stationerySection.items.length > 0) {
      navItems.push(stationerySection);
    }

    // if (barSection.items.length > 0) {
    //   navItems.push(barSection);
    // }

    return navItems;
  }, [t, authUser]);

  return data;
}
